import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { toast } from "react-toastify";
import { Storage } from 'aws-amplify';
import {
  Container,
  Form,
  Button,
  Icon,
  Divider,
  Segment,
} from "semantic-ui-react";
import { MapContainer, TileLayer, Marker, Polygon, Polyline } from "react-leaflet";
import L from "leaflet";
import * as KML_ACTIONS from '../../../actions/kmlUploadActions';

// Constants
const DEFAULT_POSITION = [-25.731340, 28.218370];
const MAP_CONFIG = {
  zoom: 15,
  scrollWheelZoom: true
};
const POLYGON_OPTIONS = { color: 'blue', fillOpacity: 0.2 };
const POLYLINE_OPTIONS = { color: 'red' };
const MARKER_ICON_CONFIG = {
  iconUrl: require("../../../media/marker.png"),
  iconSize: [38, 38],
  iconAnchor: [12, 41],
};
const KML_GEOMETRY_TYPES = ['Point', 'LineString', 'Polygon'];

// Utility Functions
const updateKmlState = (setState, updates) => {
  setState(prevState => ({
    ...prevState,
    kml: { ...prevState.kml, ...updates },
    isChanged: true
  }));
};

const updateState = (setState, updates) => {
  setState(prevState => ({ ...prevState, ...updates }));
};

const coordinatesToWKT = (coordStr, type) => {
  const coords = coordStr.trim().split(/\s+/).map(pair => {
    const [lon, lat] = pair.split(',').map(Number);
    return `${lon} ${lat}`;
  });

  const wktMap = {
    Point: `POINT(${coords[0]})`,
    LineString: `LINESTRING(${coords.join(', ')})`,
    Polygon: `POLYGON((${coords.join(', ')}))`
  };

  return wktMap[type] || null;
};

const convertToDecimalStrings = (arr) =>
  arr.map(item => ({
    ...item,
    geocode: item.geocode?.map(coord => coord?.toString()),
    coordinates: item.coordinates?.map(coordArr =>
      Array.isArray(coordArr)
        ? coordArr.map(coord => coord?.toString())
        : coordArr
    ),
  }));

const readKmlFileContent = (kmlFile) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(kmlFile);
  });
};

export function KmlDetails(props) {
  const [state, setState] = useState({
    kml: { ...props.kml },
    isChanged: false,
    markers: [],
    polygons: [],
    polylines: [],
    wkt: ''
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
  });

  useEffect(() => {
    updateState(setState, {
      kml: { ...props.kml },
      isChanged: false,
    });
  }, [props.kml]);

  useEffect(() => {
    if (!props.isNew && !_.isEmpty(props.kml.fileName)) {
      fetchKmlFileFromS3();
    }
  }, [props.isNew, props.kml.fileName]);

  const fetchKmlFileFromS3 = async () => {
    try {
      const response = await fetch(await Storage.get(props.kml.filePath));
      
      if (!response.ok) {
        toast.error("Failed to fetch KML file.");
        return;
      }

      const kmlContent = await response.text();
      const kmlBlob = new Blob([kmlContent], { type: 'application/vnd.google-earth.kml+xml' });
      
      await extractFeaturesFromKml(kmlBlob);
    } catch (error) {
      toast.error("Error fetching KML file from S3");
    }
  };

  const extractFeaturesFromKml = async (kmlFile) => {
    try {
      const kmlContent = await readKmlFileContent(kmlFile);
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(kmlContent, "text/xml");
      const placemarks = xmlDoc.querySelectorAll("Placemark");

      const updatedState = {
        markers: [],
        polygons: [],
        polylines: [],
        wkt: ''
      };

      // Extract WKT
      KML_GEOMETRY_TYPES.forEach(type => {
        const elements = xmlDoc.getElementsByTagName(type);
        for (let i = 0; i < elements.length; i++) {
          const coords = elements[i].getElementsByTagName('coordinates')[0];
          if (coords) {
            const wkt = coordinatesToWKT(coords.textContent, type);
            if (wkt) updatedState.wkt = wkt;
          }
        }
      });

      // Extract placemarks
      placemarks.forEach((placemark) => {
        const coordinatesElement = placemark.querySelector("coordinates");
        if (!coordinatesElement) return;

        const coordinates = coordinatesElement.textContent.split(",");
        const geocode = [parseFloat(coordinates[1]), parseFloat(coordinates[0])];
        
        const type = placemark.querySelector("Point") ? "Point"
          : placemark.querySelector("Polygon") ? "Polygon"
          : placemark.querySelector("LineString") ? "LineString"
          : null;

        if (type === "Point") {
          updatedState.markers.push({ geocode });
        } else if (type === "Polygon") {
          const polygonCoordinates = Array.from(placemark.querySelectorAll("coordinates"))
            .flatMap((coord) =>
              coord.textContent.trim().split(" ").map((point) => {
                const [longitude, latitude] = point.split(",").map(parseFloat);
                return [latitude, longitude];
              })
            );
          updatedState.polygons.push({ coordinates: polygonCoordinates });
        } else if (type === "LineString") {
          const polylineCoordinates = coordinatesElement.textContent
            .trim()
            .split(' ')
            .map(point => {
              const [longitude, latitude] = point.split(',').map(parseFloat);
              return [latitude, longitude];
            });
          updatedState.polylines.push({ coordinates: polylineCoordinates });
        }
      });

      updateState(setState, updatedState);
      return updatedState;
    } catch (error) {
      toast.error("Error extracting features from KML");
      throw error;
    }
  };

  const onSubmit = (data) => {
    const toCommit = {
      name: data.name || '',
      description: data.description || '',
      fileName: data.kmlFile?.[0]?.name || props.kml?.fileName || '',
      kmlMarkers: convertToDecimalStrings(state.markers),
      kmlPolygons: convertToDecimalStrings(state.polygons),
      kmlPolylines: convertToDecimalStrings(state.polylines),
      wkt: state.wkt
    };

    const kmlFile = data.kmlFile?.[0];
    const finalKml = props.isNew ? toCommit : { ...props.kml, ...toCommit };

    props.saveKmlFile(props.tenant, finalKml, kmlFile, props.isNew);
    updateState(setState, { isChanged: false });
    if (props.onComplete) props.onComplete();
  };

  const handleKmlFileSelection = async (e) => {
    const kmlFile = e.target.files[0];
    if (!kmlFile?.name.endsWith('.kml')) {
      toast.error("Please upload a Google Earth generated .kml file.");
      return;
    }

    try {
      await extractFeaturesFromKml(kmlFile);
      updateKmlState(setState, { kmlFile });
    } catch (error) {
      toast.error("Error processing KML file");
    }
  };

  const defaultIcon = new L.Icon(MARKER_ICON_CONFIG);

  const MapDisplay = () => {
    if (!state.markers.length && !state.polygons.length && !state.polylines.length) {
      return null;
    }

    // Safely get center position with proper validation
    let centerPosition = DEFAULT_POSITION;
    
    if (state.markers.length > 0 && state.markers[0]?.geocode) {
      centerPosition = state.markers[0].geocode;
    } else if (state.polygons.length > 0 && state.polygons[0]?.coordinates?.length > 0) {
      centerPosition = state.polygons[0].coordinates[0];
    } else if (state.polylines.length > 0 && state.polylines[0]?.coordinates?.length > 0) {
      centerPosition = state.polylines[0].coordinates[0];
    }

    // Ensure centerPosition is valid
    if (!centerPosition || !Array.isArray(centerPosition) || centerPosition.length < 2) {
      centerPosition = DEFAULT_POSITION;
    }

    return (
      <MapContainer center={centerPosition} {...MAP_CONFIG}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {state.markers.map((marker, index) => (
          <Marker 
            key={`marker-${index}`} 
            position={marker.geocode} 
            icon={defaultIcon} 
          />
        ))}

        {state.polygons.map((polygon, index) => (
          <Polygon
            key={`polygon-${index}`}
            pathOptions={POLYGON_OPTIONS}
            positions={polygon.coordinates}
          />
        ))}

        {state.polylines.map((polyline, index) => (
          <Polyline 
            key={`polyline-${index}`} 
            positions={polyline.coordinates} 
            {...POLYLINE_OPTIONS}
          />
        ))}
      </MapContainer>
    );
  };

  return (
    <Container>
      <Segment>
        <Form size="small" onSubmit={handleSubmit(onSubmit)} style={{ paddingBottom: "50px" }}>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Name</label>
              <input
                name="name"
                className={errors.name ? "invalid-input-field" : ""}
                {...register("name", { required: true, maxLength: 128 })}
                placeholder="Name"
                value={state.kml.name || ''}
                onChange={(e) => updateKmlState(setState, { name: e.target.value })}
              />
              {errors.name?.type === "required" && (
                <p className="validation-error">* Please enter a name</p>
              )}
              {errors.name?.type === "maxLength" && (
                <p className="validation-error">* Name may not be more than 128 characters long</p>
              )}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>Description</label>
              <input
                name="description"
                className={errors.description ? "invalid-input-field" : ""}
                {...register("description", { maxLength: 255 })}
                placeholder="Description"
                value={state.kml.description || ''}
                onChange={(e) => updateKmlState(setState, { description: e.target.value })}
              />
              {errors.description?.type === "maxLength" && (
                <p className="validation-error">* Description may not be more than 255 characters long</p>
              )}
            </Form.Field>
          </Form.Group>

          <Form.Group widths="equal">
            <Form.Field>
              <label>KML File</label>
              {state.kml?.fileName && (
                <label>Uploaded File: {state.kml.fileName}</label>
              )}
              <input
                type="file"
                accept=".kml"
                name="kmlFile"
                className={errors.kmlFile ? "invalid-input-field" : ""}
                {...register("kmlFile", { required: false })}
                onChange={handleKmlFileSelection}
              />
              {errors.kmlFile?.type === "required" && (
                <p className="validation-error">* Please select a KML file</p>
              )}
            </Form.Field>
          </Form.Group>

          <Divider />
          <Button.Group floated="right">
            <Button 
              type="button"
              className="danger" 
              onClick={(e) => {
                e.preventDefault();
                if (props.onComplete) props.onComplete();
              }}
            >
              <Icon name="remove" /> Cancel
            </Button>
            <Button type="submit" className="success" disabled={!state.isChanged}>
              <Icon name="checkmark" /> Save
            </Button>
          </Button.Group>
        </Form>
      </Segment>
      
      <MapDisplay />
    </Container>
  );
}

const mapStateToProps = (state, ownProps) => {
  const newKml = {
    name: '',
    description: '',
    fileName: '',
  };

  const isNew = ownProps.bdaction === 'add';
  const kml = isNew ? newKml : (ownProps.kml || newKml);

  return {
    tenant: state.tenantManagement.activeTenant,
    isNew,
    kml,
    userPermissions: _.isEmpty(state.authUserRoles?.permissions) 
      ? {} 
      : state.authUserRoles.permissions,
    isPermissionsFetched: state.isPermissionsFetched === true,
  };
};

export default connect(
  mapStateToProps,
  (dispatch) => ({
    saveKmlFile: (tenant, file, kmlFile, isNew) => 
      dispatch(KML_ACTIONS.saveKmlFileAction(tenant, file, kmlFile, isNew)),
  })
)(KmlDetails);
